import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Grid,
  FormControlLabel,
  Checkbox,
  Button,
  TextField,
  Box,
  Collapse,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Link,
  Tooltip,
  Divider,
  Switch,
} from '@mui/material';
import UpgradeRequestDialog from '../UpgradeRequestDialog';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  RestartAlt as RestartAltIcon,
} from '@mui/icons-material';
import { LINE } from '../../constants/brand';

// Save Search Dialog Component
const SaveSearchDialog = ({ 
  open, 
  onClose, 
  searchParams, 
  activityDateFilters,
  wellDateFilters,
  wellGroupFilters,
  dateFiltersExpanded,
  mainFiltersExpanded,
  otherFiltersExpanded,
  multiLineEnabled,
  saveSearchName,
  setSaveSearchName,
  savingSearch,
  setSavingSearch,
  saveError,
  setSaveError,
  saveSuccess,
  setSaveSuccess,
  handleSaveSearch,
  onSaveSuccess 
}) => {
  const handleClose = () => {
    // Close dialog - state will be reset after dialog closes
    onClose();
  };

  const handleExited = () => {
    // Reset state after dialog close animation completes
    setSaveSearchName('');
    setSaveError('');
    setSaveSuccess(false);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      TransitionProps={{ onExited: handleExited }}
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle>Save Search</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Save your current search parameters for quick access later.
        </DialogContentText>
        
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        )}
        
        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Search saved successfully!
          </Alert>
        )}
        
        {!saveSuccess && (
          <TextField
            autoFocus
            margin="dense"
            label="Search Name"
            fullWidth
            variant="outlined"
            value={saveSearchName}
            onChange={(e) => setSaveSearchName(e.target.value)}
            required
          />
        )}
      </DialogContent>
      <DialogActions>
        {saveSuccess ? (
          <Button onClick={handleClose} variant="contained" color="primary">
            Close
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={savingSearch}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveSearch} 
              variant="contained" 
              disabled={!saveSearchName.trim() || savingSearch}
            >
              {savingSearch ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

const SearchForm = ({
  // State props
  searchParams,
  setSearchParams,
  inputValues,
  setInputValues,
  mainFiltersExpanded,
  setMainFiltersExpanded,
  multiLineEnabled,
  setMultiLineEnabled,
  otherFiltersExpanded,
  setOtherFiltersExpanded,
  wellGroupFilters,
  setWellGroupFilters,
  activityDateFilters,
  setActivityDateFilters,
  wellDateFilters,
  setWellDateFilters,
  dateFiltersExpanded,
  setDateFiltersExpanded,
  loading,
  
  // New expand/collapse state
  searchFormExpanded = true,
  setSearchFormExpanded = () => {}, // Default no-op function
  
  // Search results for filter options
  searchResults,
  
  // Results filter (for clearing filter on new search)
  resultsFilter,
  
  // Handler props
  handleInputChange,
  handleInputBlur,
  toggleMultiLine,
  toggleWellGroupFilter,
  handleSearch,
  executeSearch,
  handleReset,
  cancelSearch,
  validateSearchParameters,
  
  // Dialog props
  saveSearchDialogOpen,
  setSaveSearchDialogOpen,
  aboutSearchDialogOpen,
  setAboutSearchDialogOpen,
  refineSearchDialogOpen,
  setRefineSearchDialogOpen,
  setWasRefineSearch,
  
  // Save search props
  saveSearchName,
  setSaveSearchName,
  savingSearch,
  setSavingSearch,
  saveError,
  setSaveError,
  saveSuccess,
  setSaveSuccess,
  handleSaveSearch,
  tierInfo,
  
  // Dialog mode prop
  isDialogMode = false,
  originalSearchParams,
  onDialogClose,
}) => {
  // Info dialog state
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [infoDialogContent, setInfoDialogContent] = useState({ title: '', description: '' });
  
  // Upgrade request dialog state
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [shouldTriggerRefineSearch, setShouldTriggerRefineSearch] = useState(false);
  const [selectedCounties, setSelectedCounties] = useState(new Set());
  const [selectedOperators, setSelectedOperators] = useState(new Set());
  const [selectedOperatorNumbers, setSelectedOperatorNumbers] = useState(new Set());
  const [selectedLeaseNames, setSelectedLeaseNames] = useState(new Set());
  const [selectedLeaseNumbers, setSelectedLeaseNumbers] = useState(new Set());
  const [selectedApiNumbers, setSelectedApiNumbers] = useState(new Set());
  
  // Parameter enable/disable state
  const [enabledParameters, setEnabledParameters] = useState({
    county: false,
    operator: false,
    operatorNumber: false,
    leaseName: false,
    leaseNumber: false,
    apiNumber: false
  });
  
  // Get available filter options and data relationships from search results
  const getAvailableFilterOptions = React.useCallback(() => {
    const counties = new Set();
    const operators = new Set();
    const operatorNumbers = new Set();
    const leaseNames = new Set();
    const leaseNumbers = new Set();
    const apiNumbers = new Set();
    
    // Data relationships: lease name -> all other parameters
    const leaseRelationships = new Map();
    
    if (searchResults && searchResults.data) {
      // Extract API numbers ONLY from Wells table
      if (searchResults.data.wells && Array.isArray(searchResults.data.wells)) {
        searchResults.data.wells.forEach(record => {
          const apiNumber = record['API Number'] || record.api_number || record.api || '';
          if (apiNumber && apiNumber.toString().trim() !== '') {
            apiNumbers.add(apiNumber.toString().trim());
          }
        });
      }
      
      // Extract all other parameters from all data tables
      Object.values(searchResults.data).forEach(tableData => {
        if (Array.isArray(tableData)) {
          tableData.forEach(record => {
            const county = record.county || record['County'] || '';
            const operator = record.operator_name || record['Operator Name'] || record.operator;
            const operatorNumber = record.operator_number || record['Operator Number'] || '';
            const leaseName = record.lease_name || record['Lease Name'] || '';
            const leaseNumber = record.lease_number || record['Lease Number'] || '';
            
            // Add to sets (skip API number here since it's extracted from Wells only)
            if (county && county.toString().trim() !== '') {
              counties.add(county.toString().trim());
            }
            if (operator && operator.toString().trim() !== '') {
              operators.add(operator.toString().trim());
            }
            if (operatorNumber && operatorNumber.toString().trim() !== '') {
              operatorNumbers.add(operatorNumber.toString().trim());
            }
            if (leaseName && leaseName.toString().trim() !== '') {
              leaseNames.add(leaseName.toString().trim());
            }
            if (leaseNumber && leaseNumber.toString().trim() !== '') {
              leaseNumbers.add(leaseNumber.toString().trim());
            }
            
            // Build relationships - lease name is the key
            if (leaseName && leaseName.toString().trim() !== '') {
              const leaseKey = leaseName.toString().trim();
              if (!leaseRelationships.has(leaseKey)) {
                leaseRelationships.set(leaseKey, {
                  counties: new Set(),
                  operators: new Set(),
                  operatorNumbers: new Set(),
                  leaseNumbers: new Set()
                });
              }
              
              const relationship = leaseRelationships.get(leaseKey);
              if (county) relationship.counties.add(county.toString().trim());
              if (operator) relationship.operators.add(operator.toString().trim());
              if (operatorNumber) relationship.operatorNumbers.add(operatorNumber.toString().trim());
              if (leaseNumber) relationship.leaseNumbers.add(leaseNumber.toString().trim());
            }
          });
        }
      });
    }
    
    return {
      counties: Array.from(counties).sort(),
      operators: Array.from(operators).sort(),
      operatorNumbers: Array.from(operatorNumbers).sort(),
      leaseNames: Array.from(leaseNames).sort(),
      leaseNumbers: Array.from(leaseNumbers).sort(),
      apiNumbers: Array.from(apiNumbers).sort(),
      relationships: leaseRelationships
    };
  }, [searchResults]);

  // Reset Refine Search dialog state (not search parameters)
  const handleResetRefineSearch = React.useCallback(() => {
    const options = getAvailableFilterOptions();
    // Reset selections to all available options
    setSelectedCounties(new Set(options.counties));
    setSelectedOperators(new Set(options.operators));
    setSelectedOperatorNumbers(new Set(options.operatorNumbers));
    setSelectedLeaseNames(new Set(options.leaseNames));
    setSelectedLeaseNumbers(new Set(options.leaseNumbers));
    setSelectedApiNumbers(new Set(options.apiNumbers));
    // Clear search inputs
    setCountySearch('');
    setOperatorSearch('');
    setOperatorNumberSearch('');
    setLeaseNameSearch('');
    setLeaseNumberSearch('');
    setApiNumberSearch('');
    // Reset all parameters to disabled state
    setEnabledParameters({
      county: false,
      operator: false,
      operatorNumber: false,
      leaseName: false,
      leaseNumber: false,
      apiNumber: false
    });
  }, [getAvailableFilterOptions]);
  
  // Initialize with all options selected by default, or clear if no search results
  React.useEffect(() => {
    const options = getAvailableFilterOptions();
    
    // If no search results, clear all selections
    if (!searchResults || !searchResults.data || Object.keys(searchResults.data).length === 0) {
      setSelectedCounties(new Set());
      setSelectedOperators(new Set());
      setSelectedOperatorNumbers(new Set());
      setSelectedLeaseNames(new Set());
      setSelectedLeaseNumbers(new Set());
      setSelectedApiNumbers(new Set());
      return;
    }
    
    // If we have search results, initialize selections
    if (options.counties.length > 0 && selectedCounties.size === 0) {
      setSelectedCounties(new Set(options.counties));
    }
    if (options.operators.length > 0 && selectedOperators.size === 0) {
      setSelectedOperators(new Set(options.operators));
    }
    if (options.operatorNumbers.length > 0 && selectedOperatorNumbers.size === 0) {
      setSelectedOperatorNumbers(new Set(options.operatorNumbers));
    }
    if (options.leaseNames.length > 0 && selectedLeaseNames.size === 0) {
      setSelectedLeaseNames(new Set(options.leaseNames));
    }
    if (options.leaseNumbers.length > 0 && selectedLeaseNumbers.size === 0) {
      setSelectedLeaseNumbers(new Set(options.leaseNumbers));
    }
    if (options.apiNumbers.length > 0 && selectedApiNumbers.size === 0) {
      setSelectedApiNumbers(new Set(options.apiNumbers));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults, getAvailableFilterOptions]);

  // Reset filter selections when search completes (when loading changes from true to false)
  React.useEffect(() => {
    if (!loading && searchResults) {
      // Search just completed - reset Refine Search dialog to disabled state
      handleResetRefineSearch();
    }
  }, [loading, searchResults, handleResetRefineSearch]);

  // Trigger search after refine parameters are applied (skip activity date confirm)
  useEffect(() => {
    if (shouldTriggerRefineSearch) {
      setShouldTriggerRefineSearch(false);
      // Clear filter when refine search is triggered
      if (resultsFilter) {
        resultsFilter.clearFilter();
      }
      if (executeSearch) {
        executeSearch();
      } else {
        handleSearch();
      }
    }
  }, [shouldTriggerRefineSearch, handleSearch, executeSearch, resultsFilter]);
  
  // Search filters for the lists
  const [countySearch, setCountySearch] = useState('');
  const [operatorSearch, setOperatorSearch] = useState('');
  const [operatorNumberSearch, setOperatorNumberSearch] = useState('');
  const [leaseNameSearch, setLeaseNameSearch] = useState('');
  const [leaseNumberSearch, setLeaseNumberSearch] = useState('');
  const [apiNumberSearch, setApiNumberSearch] = useState('');

  // Toggle parameter enable/disable with mutual exclusivity for Well API Number
  const toggleParameterEnabled = (parameterType) => {
    setEnabledParameters(prev => {
      const newValue = !prev[parameterType];
      
      if (parameterType === 'apiNumber' && newValue) {
        // If enabling API Number, disable all others
        return {
          county: false,
          operator: false,
          operatorNumber: false,
          leaseName: false,
          leaseNumber: false,
          apiNumber: true
        };
      } else if (parameterType !== 'apiNumber' && newValue) {
        // If enabling any other parameter, disable API Number
        return {
          ...prev,
          [parameterType]: true,
          apiNumber: false
        };
      } else {
        // Just toggle the parameter
        return {
          ...prev,
          [parameterType]: newValue
        };
      }
    });
  };

  // Hierarchical filtering logic - mutual interaction based on lease names
  const updateSelectionsBasedOnLeaseNames = (updatedLeaseNames) => {
    const options = getAvailableFilterOptions();
    const relationships = options.relationships;
    
    // Get all lease names that are currently selected
    const activeLeaseNames = updatedLeaseNames;
    
    // Find all related parameters for the active lease names
    const relatedCounties = new Set();
    const relatedOperators = new Set();
    const relatedOperatorNumbers = new Set();
    const relatedLeaseNumbers = new Set();
    
    activeLeaseNames.forEach(leaseName => {
      if (relationships.has(leaseName)) {
        const relationship = relationships.get(leaseName);
        // Always collect relationships regardless of enabled state
        relationship.counties.forEach(county => relatedCounties.add(county));
        relationship.operators.forEach(operator => relatedOperators.add(operator));
        relationship.operatorNumbers.forEach(opNumber => relatedOperatorNumbers.add(opNumber));
        relationship.leaseNumbers.forEach(leaseNumber => relatedLeaseNumbers.add(leaseNumber));
      }
    });
    
    // Update all selections to only include parameters related to active lease names
    // Always update all parameters for hierarchical consistency
    setSelectedCounties(relatedCounties);
    setSelectedOperators(relatedOperators);
    setSelectedOperatorNumbers(relatedOperatorNumbers);
    setSelectedLeaseNames(activeLeaseNames);
    setSelectedLeaseNumbers(relatedLeaseNumbers);
  };
  
  const updateLeaseNamesBasedOnParameter = (parameterType, parameterValue, isSelected) => {
    const options = getAvailableFilterOptions();
    const relationships = options.relationships;
    
    if (isSelected) {
      // When selecting a parameter, add all lease names that contain this parameter
      const newLeaseNames = new Set(selectedLeaseNames);
      
      relationships.forEach((relationship, leaseName) => {
        let shouldAddLease = false;
        
        switch (parameterType) {
          case 'county':
            if (relationship.counties.has(parameterValue)) shouldAddLease = true;
            break;
          case 'operator':
            if (relationship.operators.has(parameterValue)) shouldAddLease = true;
            break;
          case 'operatorNumber':
            if (relationship.operatorNumbers.has(parameterValue)) shouldAddLease = true;
            break;
                case 'leaseNumber':
                  if (relationship.leaseNumbers.has(parameterValue)) shouldAddLease = true;
                  break;
                default:
                  shouldAddLease = false;
                  break;
        }
        
        if (shouldAddLease) {
          newLeaseNames.add(leaseName);
        }
      });
      
      setSelectedLeaseNames(newLeaseNames);
      updateSelectionsBasedOnLeaseNames(newLeaseNames);
    } else {
      // When deselecting a parameter, remove lease names that only contain this parameter
      const newLeaseNames = new Set();
      
      selectedLeaseNames.forEach(leaseName => {
        if (relationships.has(leaseName)) {
          const relationship = relationships.get(leaseName);
          let shouldKeepLease = false;
          
          // Check if this lease has any other selected parameters
          switch (parameterType) {
            case 'county':
              shouldKeepLease = Array.from(relationship.counties).some(county => 
                county !== parameterValue && selectedCounties.has(county)
              );
              break;
            case 'operator':
              shouldKeepLease = Array.from(relationship.operators).some(operator => 
                operator !== parameterValue && selectedOperators.has(operator)
              );
              break;
            case 'operatorNumber':
              shouldKeepLease = Array.from(relationship.operatorNumbers).some(opNumber => 
                opNumber !== parameterValue && selectedOperatorNumbers.has(opNumber)
              );
              break;
                case 'leaseNumber':
                  shouldKeepLease = Array.from(relationship.leaseNumbers).some(leaseNumber => 
                    leaseNumber !== parameterValue && selectedLeaseNumbers.has(leaseNumber)
                  );
                  break;
                default:
                  shouldKeepLease = false;
                  break;
          }
          
          if (shouldKeepLease) {
            newLeaseNames.add(leaseName);
          }
        }
      });
      
      setSelectedLeaseNames(newLeaseNames);
      updateSelectionsBasedOnLeaseNames(newLeaseNames);
    }
  };

  // Filter functionality
  const handleApplyFilters = () => {
    // Update search parameters with selected filters
    const updatedParams = { ...searchParams };
    
    // Helper to wrap values in quotes for exact matching
    const wrapForExactMatch = (values) => {
      return Array.from(values).map(val => `"${val}"`).join('\n');
    };
    
    // If refining by well API, zero out all other search params
    if (enabledParameters.apiNumber) {
      // Clear all other search parameters
      updatedParams.operator_name = '';
      updatedParams.operator_number = '';
      updatedParams.lease_name = '';
      updatedParams.lease_number = '';
      updatedParams.county = '';
      
      // Set API numbers if any are selected
      if (selectedApiNumbers.size > 0) {
        updatedParams.api_number = wrapForExactMatch(selectedApiNumbers);
      } else {
        updatedParams.api_number = '';
      }
    } else {
      // Refining by something other than API - clear API numbers and update only enabled parameters
      // Clear API number when refining by other parameters
      updatedParams.api_number = '';
      
      // Add counties if any are selected and enabled - wrap in quotes for exact match
      if (enabledParameters.county && selectedCounties.size > 0) {
        updatedParams.county = wrapForExactMatch(selectedCounties);
      } else if (enabledParameters.county) {
        updatedParams.county = '';
      }
      
      // Add operator names if any are selected and enabled
      if (enabledParameters.operator && selectedOperators.size > 0) {
        updatedParams.operator_name = wrapForExactMatch(selectedOperators);
      } else if (enabledParameters.operator) {
        updatedParams.operator_name = '';
      }
      
      // Add operator numbers if any are selected and enabled
      if (enabledParameters.operatorNumber && selectedOperatorNumbers.size > 0) {
        updatedParams.operator_number = wrapForExactMatch(selectedOperatorNumbers);
      } else if (enabledParameters.operatorNumber) {
        updatedParams.operator_number = '';
      }
      
      // Add lease names if any are selected and enabled
      if (enabledParameters.leaseName && selectedLeaseNames.size > 0) {
        updatedParams.lease_name = wrapForExactMatch(selectedLeaseNames);
      } else if (enabledParameters.leaseName) {
        updatedParams.lease_name = '';
      }
      
      // Add lease numbers if any are selected and enabled
      if (enabledParameters.leaseNumber && selectedLeaseNumbers.size > 0) {
        updatedParams.lease_number = wrapForExactMatch(selectedLeaseNumbers);
      } else if (enabledParameters.leaseNumber) {
        updatedParams.lease_number = '';
      }
    }
    
    setSearchParams(updatedParams);
    
    // Update input values to show selections in the form fields (with quotes)
    const updatedInputValues = { ...inputValues };
    
    // If refining by well API, zero out all other input values
    if (enabledParameters.apiNumber) {
      updatedInputValues.operator_name = '';
      updatedInputValues.operator_number = '';
      updatedInputValues.lease_name = '';
      updatedInputValues.lease_number = '';
      updatedInputValues.county = '';
      
      // Set API numbers if any are selected
      if (selectedApiNumbers.size > 0) {
        updatedInputValues.api_number = wrapForExactMatch(selectedApiNumbers);
      } else {
        updatedInputValues.api_number = '';
      }
    } else {
      // Refining by something other than API - clear API numbers and update only enabled parameters
      // Clear API number when refining by other parameters
      updatedInputValues.api_number = '';
      
      // Update county field
      if (enabledParameters.county && selectedCounties.size > 0) {
        updatedInputValues.county = wrapForExactMatch(selectedCounties);
      } else if (enabledParameters.county) {
        updatedInputValues.county = '';
      }
      
      // Update operator name field
      if (enabledParameters.operator && selectedOperators.size > 0) {
        updatedInputValues.operator_name = wrapForExactMatch(selectedOperators);
      } else if (enabledParameters.operator) {
        updatedInputValues.operator_name = '';
      }
      
      // Update operator number field
      if (enabledParameters.operatorNumber && selectedOperatorNumbers.size > 0) {
        updatedInputValues.operator_number = wrapForExactMatch(selectedOperatorNumbers);
      } else if (enabledParameters.operatorNumber) {
        updatedInputValues.operator_number = '';
      }
      
      // Update lease name field
      if (enabledParameters.leaseName && selectedLeaseNames.size > 0) {
        updatedInputValues.lease_name = wrapForExactMatch(selectedLeaseNames);
      } else if (enabledParameters.leaseName) {
        updatedInputValues.lease_name = '';
      }
      
      // Update lease number field
      if (enabledParameters.leaseNumber && selectedLeaseNumbers.size > 0) {
        updatedInputValues.lease_number = wrapForExactMatch(selectedLeaseNumbers);
      } else if (enabledParameters.leaseNumber) {
        updatedInputValues.lease_number = '';
      }
    }
    
    setInputValues(updatedInputValues);
    
    // Expand search parameters and main filters to show the applied filters
    if (setSearchFormExpanded) {
    setSearchFormExpanded(true);
    }
    setMainFiltersExpanded(true);
    
    // Enable multi-line view if more than one value was selected for any parameter
    if (selectedCounties.size > 1 || selectedOperators.size > 1 || selectedOperatorNumbers.size > 1 || selectedLeaseNames.size > 1 || selectedLeaseNumbers.size > 1 || selectedApiNumbers.size > 1) {
      setMultiLineEnabled(true);
    }
    
    setRefineSearchDialogOpen(false);
    
    // Mark this as a refine search so Search Params won't be hidden
    setWasRefineSearch(true);
    
    // Set flag to trigger search after state updates are applied
    setShouldTriggerRefineSearch(true);
  };

  const handleResetFilters = () => {
    // Reset to all options selected by default
    const options = getAvailableFilterOptions();
    setSelectedCounties(new Set(options.counties));
    setSelectedOperators(new Set(options.operators));
    setSelectedOperatorNumbers(new Set(options.operatorNumbers));
    setSelectedLeaseNames(new Set(options.leaseNames));
    setSelectedLeaseNumbers(new Set(options.leaseNumbers));
    setSelectedApiNumbers(new Set(options.apiNumbers));
    setCountySearch('');
    setOperatorSearch('');
    setOperatorNumberSearch('');
    setLeaseNameSearch('');
    setLeaseNumberSearch('');
    setApiNumberSearch('');
    
    // Reset all parameters to disabled state
    setEnabledParameters({
      county: false,
      operator: false,
      operatorNumber: false,
      leaseName: false,
      leaseNumber: false,
      apiNumber: false
    });
    
    // Also clear the input fields
    const updatedInputValues = { ...inputValues };
    updatedInputValues.county = '';
    updatedInputValues.operator_name = '';
    updatedInputValues.operator_number = '';
    updatedInputValues.lease_name = '';
    updatedInputValues.lease_number = '';
    updatedInputValues.api_number = '';
    setInputValues(updatedInputValues);
  };

  // Helper function to open info dialog
  const openInfoDialog = (title, description) => {
    setInfoDialogContent({ title, description });
    setInfoDialogOpen(true);
  };

  const WrapperComponent = isDialogMode ? Box : Paper;
  
  return (
    <WrapperComponent 
      {...(!isDialogMode && { elevation: 0 })}
      sx={{ 
        p: { xs: 2, md: 3 }, 
        mb: isDialogMode ? 0 : 3,
        width: '100%',
        overflow: 'hidden',
        position: 'relative',
        ...(!isDialogMode && {
          border: `1px solid ${LINE}`,
          borderRadius: 2,
          bgcolor: '#fff',
        })
      }}>
      
      <Grid container spacing={2} sx={{ width: '100%', overflow: 'hidden' }}>
        
        {/* Table Selection */}
        <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">
                Select dataset to search:
              </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setSearchParams({
                    ...searchParams,
                    search_wells: true,
                    search_production: true,
                    search_inspections: true,
                    search_violations: tierInfo?.table_access?.violations || false,
                    search_severances: tierInfo?.table_access?.severances || false,
                    search_spills: tierInfo?.table_access?.spills || false
                  });
                }}
                sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.75rem' }}
              >
                All
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setSearchParams({
                    ...searchParams,
                    search_wells: searchParams.state === 'Texas' ? true : false,
                    search_production: false,
                    search_inspections: false,
                    search_violations: false,
                    search_severances: false,
                    search_spills: false
                  });
                }}
                sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.75rem' }}
              >
                None
              </Button>
            </Box>
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={searchParams.state === 'Texas' ? true : searchParams.search_wells}
                disabled={searchParams.state === 'Texas'}
                onChange={(e) => {
                  if (searchParams.state !== 'Texas') {
                    setSearchParams({ ...searchParams, search_wells: e.target.checked });
                  }
                }}
              />
            }
            label="Wells"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={searchParams.search_production}
                onChange={(e) => setSearchParams({ ...searchParams, search_production: e.target.checked })}
              />
            }
            label="Production"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={searchParams.search_inspections}
                onChange={(e) => setSearchParams({ ...searchParams, search_inspections: e.target.checked })}
              />
            }
            label="Inspections"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={searchParams.search_violations}
                onChange={(e) => setSearchParams({ ...searchParams, search_violations: e.target.checked })}
                disabled={tierInfo && !tierInfo.table_access?.violations}
              />
            }
            label="Violations"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={searchParams.search_severances}
                onChange={(e) => setSearchParams({ ...searchParams, search_severances: e.target.checked })}
                disabled={tierInfo && !tierInfo.table_access?.severances}
              />
            }
            label="Severances"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={searchParams.search_spills}
                onChange={(e) => setSearchParams({ ...searchParams, search_spills: e.target.checked })}
                disabled={tierInfo && !tierInfo.table_access?.spills}
              />
            }
            label="Spills"
          />
        </Grid>
        
        {/* Pro Access Note */}
        {tierInfo && tierInfo.tier === 'basic' && (
          <Grid item xs={12}>
            <Typography variant="body2" color="primary.main" sx={{ 
              mt: 1, 
              mb: 1, 
              fontWeight: 500 
            }}>
              💡 <Link 
                component="button"
                onClick={() => setUpgradeDialogOpen(true)}
                sx={{ 
                  color: 'primary.main',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit'
                }}
              >
                Upgrade to Pro to have access to all tables
              </Link>
            </Typography>
          </Grid>
        )}
        
        {/* Main Filters Section */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, mb: 0.5 }}>
            At least one Main Filters search criteria is required (multiple entries allowed, place each entry on a new line):
          </Typography>
        </Grid>
        
        {/* Main Filters Section */}
        <Grid item xs={12}>
          <Box sx={{ mt: 0, mb: 1, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#fafafa' }}>
            <Box 
              sx={{ 
                p: { xs: 1, sm: 2 }, 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                '&:hover': { backgroundColor: '#f0f0f0' }
              }}
              onClick={() => setMainFiltersExpanded(!mainFiltersExpanded)}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                Main Filters
              </Typography>
              {mainFiltersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            
            <Collapse in={mainFiltersExpanded}>
              <Box sx={{ p: { xs: 1, sm: 2 }, pt: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={multiLineEnabled}
                        onChange={toggleMultiLine}
                        size="small"
                      />
                    }
                    label="Multi-line View"
                    sx={{ mb: 0 }}
                  />
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Operator Name"
                      value={inputValues.operator_name}
                      onChange={(e) => handleInputChange('operator_name', e.target.value)}
                      onBlur={() => handleInputBlur('operator_name')}
                      placeholder="Enter operator names (one per line)"
                      multiline
                      rows={multiLineEnabled ? 6 : 1}
                      sx={{ backgroundColor: 'white' }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Operator Number"
                      value={inputValues.operator_number}
                      onChange={(e) => handleInputChange('operator_number', e.target.value)}
                      onBlur={() => handleInputBlur('operator_number')}
                      placeholder="Enter operator numbers (one per line)"
                      multiline
                      rows={multiLineEnabled ? 6 : 1}
                      sx={{ backgroundColor: 'white' }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Lease Name"
                      value={inputValues.lease_name}
                      onChange={(e) => handleInputChange('lease_name', e.target.value)}
                      onBlur={() => handleInputBlur('lease_name')}
                      placeholder="Enter lease names (one per line)"
                      multiline
                      rows={multiLineEnabled ? 6 : 1}
                      sx={{ backgroundColor: 'white' }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Lease Number"
                      value={inputValues.lease_number}
                      onChange={(e) => handleInputChange('lease_number', e.target.value)}
                      onBlur={() => handleInputBlur('lease_number')}
                      placeholder="Enter lease numbers (one per line)"
                      multiline
                      rows={multiLineEnabled ? 6 : 1}
                      sx={{ backgroundColor: 'white' }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Texas lease numbers are non-unique. Search with an additional parameter (e.g., county) and omit district prefixes — e.g., for 01-23456, search 23456.
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="County"
                      value={inputValues.county}
                      onChange={(e) => handleInputChange('county', e.target.value)}
                      onBlur={() => handleInputBlur('county')}
                      placeholder="Enter county names (one per line)"
                      multiline
                      rows={multiLineEnabled ? 6 : 1}
                      sx={{ backgroundColor: 'white' }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Well API Number"
                      value={inputValues.api_number || ''}
                      onChange={(e) => handleInputChange('api_number', e.target.value)}
                      onBlur={() => handleInputBlur('api_number')}
                      placeholder="Enter well API numbers (one per line)"
                      multiline
                      rows={multiLineEnabled ? 6 : 1}
                      sx={{ backgroundColor: 'white' }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </Box>
        </Grid>
        
        {/* Refine search section */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, mb: 0.5 }}>
            Additional filters:
          </Typography>
        </Grid>

        {/* Date Filters Section */}
        <Grid item xs={12}>
          <Box sx={{ mt: 0, mb: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#fafafa' }}>
            <Box 
              sx={{ 
                p: { xs: 1, sm: 2 }, 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                '&:hover': { backgroundColor: '#f0f0f0' }
              }}
              onClick={() => setDateFiltersExpanded(!dateFiltersExpanded)}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                Date Filters
              </Typography>
              {dateFiltersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            
            <Collapse in={dateFiltersExpanded}>
              <Box sx={{ p: { xs: 1, sm: 2 }, pt: 0 }}>
                <Grid container spacing={3}>
                  {/* Drill Date Range Filter - Wells Only */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                        Drill Date Range Filter
                      </Typography>
                      <IconButton 
                        size="small"
                        onClick={() => openInfoDialog(
                          'Drill Date Range Filter',
                          'Filter wells by drill date. This affects only the Wells dataset results. Please note that not all wells have a drill date record.',
                        )}
                        sx={{ ml: 1, color: 'text.secondary' }}
                      >
                        <InfoIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      Wells dataset only
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Start Date"
                          type="date"
                          value={wellDateFilters.start_date}
                          onChange={(e) => setWellDateFilters({ ...wellDateFilters, start_date: e.target.value })}
                          InputLabelProps={{ shrink: true }}
                          size="small"
                          sx={{ backgroundColor: 'white' }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="End Date"
                          type="date"
                          value={wellDateFilters.end_date}
                          onChange={(e) => setWellDateFilters({ ...wellDateFilters, end_date: e.target.value })}
                          InputLabelProps={{ shrink: true }}
                          size="small"
                          sx={{ backgroundColor: 'white' }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  
                  {/* Activity Date Range Filter - Other Tables */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                        Activity Date Range Filter
                      </Typography>
                      <IconButton 
                        size="small"
                        onClick={() => openInfoDialog(
                          'Activity Date Range Filter',
                          'Filter records by their activity date. This affects Production (production month), Inspections (inspection date), Violations (violation discovery date), Severances (certified letter date), and Spills (date of loss) datasets.'
                        )}
                        sx={{ ml: 1, color: 'text.secondary' }}
                      >
                        <InfoIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      Production, Inspections, Violations, Severances, Spills datasets
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Start Date"
                          type="date"
                          value={activityDateFilters.start_date}
                          onChange={(e) => setActivityDateFilters({ ...activityDateFilters, start_date: e.target.value })}
                          InputLabelProps={{ shrink: true }}
                          size="small"
                          sx={{ backgroundColor: 'white' }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="End Date"
                          type="date"
                          value={activityDateFilters.end_date}
                          onChange={(e) => setActivityDateFilters({ ...activityDateFilters, end_date: e.target.value })}
                          InputLabelProps={{ shrink: true }}
                          size="small"
                          sx={{ backgroundColor: 'white' }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                
                {/* Clear Button */}
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setWellDateFilters({ start_date: '', end_date: '' });
                      setActivityDateFilters({ start_date: '', end_date: '' });
                    }}
                    sx={{ 
                      minWidth: 'auto',
                      px: 1,
                      py: 0.5,
                      fontSize: '0.75rem',
                      textTransform: 'none'
                    }}
                  >
                    CLEAR DATES
                  </Button>
                </Box>
              </Box>
            </Collapse>
          </Box>
        </Grid>
        
        {/* Other Filters Section */}
        <Grid item xs={12}>
          <Box sx={{ mt: 0, mb: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#fafafa' }}>
            <Box 
              sx={{ 
                p: { xs: 1, sm: 2 }, 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                '&:hover': { backgroundColor: '#f0f0f0' }
              }}
              onClick={() => setOtherFiltersExpanded(!otherFiltersExpanded)}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                Other Filters
              </Typography>
              {otherFiltersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
            
            <Collapse in={otherFiltersExpanded}>
              <Box sx={{ p: { xs: 1, sm: 2 }, pt: 0 }}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                      Well Group Filter
                    </Typography>
                    <IconButton 
                      size="small"
                      onClick={() => openInfoDialog(
                        'Well Group Filter',
                        'Filter wells by their well group status. This affects only the Wells dataset results.'
                      )}
                      sx={{ ml: 1, color: 'text.secondary' }}
                    >
                      <InfoIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Wells dataset only
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {Object.keys(wellGroupFilters).map((group) => (
                      <FormControlLabel
                        key={group}
                        control={
                          <Checkbox
                            checked={wellGroupFilters[group]}
                            onChange={() => toggleWellGroupFilter(group)}
                            size="small"
                          />
                        }
                        label={group}
                      />
                    ))}
                  </Box>
                </Grid>
              </Box>
            </Collapse>
          </Box>
        </Grid>
        
        {!isDialogMode && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant={loading ? "outlined" : "contained"}
                onClick={loading ? cancelSearch : handleSearch}
                disabled={!loading && (!searchParams.state || !validateSearchParameters().isValid)}
                color="primary"
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {loading ? 'Cancel' : 'Search'}
              </Button>
              <Button variant="text" color="error" onClick={handleReset}>
                Reset
              </Button>
            </Box>
          </Grid>
        )}
        
        {tierInfo && !tierInfo.can_save_searches && (
          <Grid item xs={12}>
            <Typography variant="body2" color="primary.main" sx={{ 
              mt: 1, 
              mb: 1, 
              fontWeight: 500 
            }}>
              💡 <Link 
                component="button"
                onClick={() => setUpgradeDialogOpen(true)}
                sx={{ 
                  color: 'primary.main',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit'
                }}
              >
                Upgrade to Pro to save searches
              </Link>
            </Typography>
          </Grid>
        )}
        </Grid>

      {/* Info Dialog */}
      <Dialog 
        open={infoDialogOpen} 
        onClose={() => setInfoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {infoDialogContent.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {infoDialogContent.description}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Search Dialog */}
      <SaveSearchDialog 
        open={saveSearchDialogOpen}
        onClose={() => setSaveSearchDialogOpen(false)}
        searchParams={searchParams}
        activityDateFilters={activityDateFilters}
        wellDateFilters={wellDateFilters}
        wellGroupFilters={wellGroupFilters}
        dateFiltersExpanded={dateFiltersExpanded}
        mainFiltersExpanded={mainFiltersExpanded}
        otherFiltersExpanded={otherFiltersExpanded}
        multiLineEnabled={multiLineEnabled}
        saveSearchName={saveSearchName}
        setSaveSearchName={setSaveSearchName}
        savingSearch={savingSearch}
        setSavingSearch={setSavingSearch}
        saveError={saveError}
        setSaveError={setSaveError}
        saveSuccess={saveSuccess}
        setSaveSuccess={setSaveSuccess}
        handleSaveSearch={handleSaveSearch}
        onSaveSuccess={() => {
          setSaveSearchDialogOpen(false);
        }}
      />

      {/* Refine Search Results Dialog */}
      <Dialog
        open={refineSearchDialogOpen}
        onClose={() => setRefineSearchDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Refine Search
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Reset Refine Search" arrow>
              <IconButton
                size="small"
                color="primary"
                onClick={handleResetFilters}
              >
                <RestartAltIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Select specific parameters to refine your search. Click "Apply" to search with the refined parameters.
            </Typography>
            
            {/* Counties Section */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1">
                  County
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({selectedCounties.size} selected of {getAvailableFilterOptions().counties.length})
                  </Typography>
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={enabledParameters.county}
                      onChange={() => toggleParameterEnabled('county')}
                      disabled={enabledParameters.apiNumber}
                      size="small"
                    />
                  }
                  label=""
                  sx={{ m: 0 }}
                />
              </Box>
              {enabledParameters.county && (
                <>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                    <TextField
                      size="small"
                      placeholder="Search..."
                      value={countySearch}
                      onChange={(e) => setCountySearch(e.target.value)}
                      sx={{ flexGrow: 1, maxWidth: 300 }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const filteredCounties = getAvailableFilterOptions().counties.filter(county => 
                          county.toLowerCase().includes(countySearch.toLowerCase())
                        );
                        setSelectedCounties(new Set(filteredCounties));
                        // Trigger hierarchical filtering for all counties at once
                        const allLeaseNames = new Set();
                        filteredCounties.forEach(county => {
                          const options = getAvailableFilterOptions();
                          const relationships = options.relationships;
                          relationships.forEach((relationship, leaseName) => {
                            if (relationship.counties.has(county)) {
                              allLeaseNames.add(leaseName);
                            }
                          });
                        });
                        updateSelectionsBasedOnLeaseNames(allLeaseNames);
                      }}
                      sx={{ 
                        textTransform: 'none', 
                        minWidth: 'auto', 
                        px: 1, 
                        py: 0.5, 
                        fontSize: '0.75rem' 
                      }}
                    >
                      ALL
                    </Button>
                    <Button 
                      size="small" 
                      variant="outlined" 
                      onClick={() => {
                        // NONE should deselect ALL items for this parameter, not just filtered ones
                        setSelectedCounties(new Set());
                        // Clear all other parameters too since no counties remain
                        setSelectedOperators(new Set());
                        setSelectedOperatorNumbers(new Set());
                        setSelectedLeaseNames(new Set());
                        setSelectedLeaseNumbers(new Set());
                      }}
                      sx={{ 
                        textTransform: 'none', 
                        minWidth: 'auto', 
                        px: 1, 
                        py: 0.5, 
                        fontSize: '0.75rem' 
                      }}
                    >
                      NONE
                    </Button>
                  </Box>
                  <Box sx={{ 
                    maxHeight: 200, 
                    overflow: 'auto', 
                    border: 1, 
                    borderColor: 'divider', 
                    borderRadius: 1,
                    p: 1
                  }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {getAvailableFilterOptions().counties
                        .filter(county => 
                          county.toLowerCase().includes(countySearch.toLowerCase())
                        )
                        .map((county) => (
                          <Button
                            key={county}
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              const newSelected = new Set(selectedCounties);
                              if (newSelected.has(county)) {
                                newSelected.delete(county);
                                updateLeaseNamesBasedOnParameter('county', county, false);
                              } else {
                                newSelected.add(county);
                                updateLeaseNamesBasedOnParameter('county', county, true);
                              }
                              setSelectedCounties(newSelected);
                            }}
                             sx={{ 
                               textTransform: 'none',
                               minWidth: 'auto',
                               px: 1.5,
                               py: 0.25,
                               fontSize: '0.75rem',
                               fontWeight: 400,
                               lineHeight: 1.2,
                               backgroundColor: selectedCounties.has(county) ? 'primary.main' : 'transparent',
                               color: selectedCounties.has(county) ? 'primary.contrastText' : 'primary.main',
                               borderColor: 'primary.main',
                               '&:hover': {
                                 backgroundColor: selectedCounties.has(county) ? 'primary.dark' : 'primary.light',
                                 color: selectedCounties.has(county) ? 'primary.contrastText' : 'primary.main',
                               },
                               '@media (hover: none)': {
                                 '&:hover': {
                                   backgroundColor: selectedCounties.has(county) ? 'primary.main' : 'transparent',
                                   color: selectedCounties.has(county) ? 'primary.contrastText' : 'primary.main',
                                 }
                               }
                             }}
                          >
                            {county}
                          </Button>
                        ))}
                    </Box>
                  </Box>
                </>
              )}
            </Box>

            <Divider />

            {/* Operators Section */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1">
                  Operator Name
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({selectedOperators.size} selected of {getAvailableFilterOptions().operators.length})
                  </Typography>
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={enabledParameters.operator}
                      onChange={() => toggleParameterEnabled('operator')}
                      disabled={enabledParameters.apiNumber}
                      size="small"
                    />
                  }
                  label=""
                  sx={{ m: 0 }}
                />
              </Box>
              {enabledParameters.operator && (
                <>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={operatorSearch}
                  onChange={(e) => setOperatorSearch(e.target.value)}
                  sx={{ flexGrow: 1, maxWidth: 300 }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const filteredOperators = getAvailableFilterOptions().operators.filter(operator => 
                      operator.toLowerCase().includes(operatorSearch.toLowerCase())
                    );
                    setSelectedOperators(new Set(filteredOperators));
                    // Trigger hierarchical filtering for all operators at once
                    const allLeaseNames = new Set();
                    filteredOperators.forEach(operator => {
                      const options = getAvailableFilterOptions();
                      const relationships = options.relationships;
                      relationships.forEach((relationship, leaseName) => {
                        if (relationship.operators.has(operator)) {
                          allLeaseNames.add(leaseName);
                        }
                      });
                    });
                    updateSelectionsBasedOnLeaseNames(allLeaseNames);
                  }}
                  sx={{ 
                    textTransform: 'none', 
                    minWidth: 'auto', 
                    px: 1, 
                    py: 0.5, 
                    fontSize: '0.75rem' 
                  }}
                >
                  ALL
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    // NONE should deselect ALL items for this parameter, not just filtered ones
                    setSelectedOperators(new Set());
                    // Clear all other parameters too since no operators remain
                    setSelectedCounties(new Set());
                    setSelectedOperatorNumbers(new Set());
                    setSelectedLeaseNames(new Set());
                    setSelectedLeaseNumbers(new Set());
                  }}
                  sx={{ 
                    textTransform: 'none', 
                    minWidth: 'auto', 
                    px: 1, 
                    py: 0.5, 
                    fontSize: '0.75rem' 
                  }}
                >
                  NONE
                </Button>
              </Box>
              <Box sx={{ 
                maxHeight: 200, 
                overflow: 'auto', 
                border: 1, 
                borderColor: 'divider', 
                borderRadius: 1,
                p: 1
              }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {getAvailableFilterOptions().operators
                    .filter(operator => 
                      operator.toLowerCase().includes(operatorSearch.toLowerCase())
                    )
                    .map((operator) => (
                      <Button
                        key={operator}
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          const newSelected = new Set(selectedOperators);
                          if (newSelected.has(operator)) {
                            newSelected.delete(operator);
                            updateLeaseNamesBasedOnParameter('operator', operator, false);
                          } else {
                            newSelected.add(operator);
                            updateLeaseNamesBasedOnParameter('operator', operator, true);
                          }
                          setSelectedOperators(newSelected);
                        }}
                         sx={{ 
                           textTransform: 'none',
                           minWidth: 'auto',
                           px: 1.5,
                           py: 0.25,
                           fontSize: '0.75rem',
                           fontWeight: 400,
                           lineHeight: 1.2,
                           backgroundColor: selectedOperators.has(operator) ? 'primary.main' : 'transparent',
                           color: selectedOperators.has(operator) ? 'primary.contrastText' : 'primary.main',
                           borderColor: 'primary.main',
                           '&:hover': {
                             backgroundColor: selectedOperators.has(operator) ? 'primary.dark' : 'primary.light',
                             color: selectedOperators.has(operator) ? 'primary.contrastText' : 'primary.main',
                           },
                           '@media (hover: none)': {
                             '&:hover': {
                               backgroundColor: selectedOperators.has(operator) ? 'primary.main' : 'transparent',
                               color: selectedOperators.has(operator) ? 'primary.contrastText' : 'primary.main',
                             }
                           }
                         }}
                      >
                        {operator}
                      </Button>
                    ))}
                </Box>
                  </Box>
                </>
              )}
            </Box>

            <Divider />

            {/* Operator Numbers Section */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1">
                  Operator Number
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({selectedOperatorNumbers.size} selected of {getAvailableFilterOptions().operatorNumbers.length})
                  </Typography>
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={enabledParameters.operatorNumber}
                      onChange={() => toggleParameterEnabled('operatorNumber')}
                      disabled={enabledParameters.apiNumber}
                      size="small"
                    />
                  }
                  label=""
                  sx={{ m: 0 }}
                />
              </Box>
              {enabledParameters.operatorNumber && (
                <>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={operatorNumberSearch}
                  onChange={(e) => setOperatorNumberSearch(e.target.value)}
                  sx={{ flexGrow: 1, maxWidth: 300 }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const filteredOperatorNumbers = getAvailableFilterOptions().operatorNumbers.filter(opNumber => 
                      opNumber.toLowerCase().includes(operatorNumberSearch.toLowerCase())
                    );
                    setSelectedOperatorNumbers(new Set(filteredOperatorNumbers));
                    // Trigger hierarchical filtering for all operator numbers at once
                    const allLeaseNames = new Set();
                    filteredOperatorNumbers.forEach(opNumber => {
                      const options = getAvailableFilterOptions();
                      const relationships = options.relationships;
                      relationships.forEach((relationship, leaseName) => {
                        if (relationship.operatorNumbers.has(opNumber)) {
                          allLeaseNames.add(leaseName);
                        }
                      });
                    });
                    updateSelectionsBasedOnLeaseNames(allLeaseNames);
                  }}
                  sx={{ 
                    textTransform: 'none', 
                    minWidth: 'auto', 
                    px: 1, 
                    py: 0.5, 
                    fontSize: '0.75rem' 
                  }}
                >
                  ALL
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    // NONE should deselect ALL items for this parameter, not just filtered ones
                    setSelectedOperatorNumbers(new Set());
                    // Clear all other parameters too since no operator numbers remain
                    setSelectedCounties(new Set());
                    setSelectedOperators(new Set());
                    setSelectedLeaseNames(new Set());
                    setSelectedLeaseNumbers(new Set());
                  }}
                  sx={{ 
                    textTransform: 'none', 
                    minWidth: 'auto', 
                    px: 1, 
                    py: 0.5, 
                    fontSize: '0.75rem' 
                  }}
                >
                  NONE
                </Button>
              </Box>
              <Box sx={{ 
                maxHeight: 200, 
                overflow: 'auto', 
                border: 1, 
                borderColor: 'divider', 
                borderRadius: 1,
                p: 1
              }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {getAvailableFilterOptions().operatorNumbers
                    .filter(opNumber => 
                      opNumber.toLowerCase().includes(operatorNumberSearch.toLowerCase())
                    )
                    .map((opNumber) => (
                      <Button
                        key={opNumber}
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          const newSelected = new Set(selectedOperatorNumbers);
                          if (newSelected.has(opNumber)) {
                            newSelected.delete(opNumber);
                            updateLeaseNamesBasedOnParameter('operatorNumber', opNumber, false);
                          } else {
                            newSelected.add(opNumber);
                            updateLeaseNamesBasedOnParameter('operatorNumber', opNumber, true);
                          }
                          setSelectedOperatorNumbers(newSelected);
                        }}
                         sx={{ 
                           textTransform: 'none',
                           minWidth: 'auto',
                           px: 1.5,
                           py: 0.25,
                           fontSize: '0.75rem',
                           fontWeight: 400,
                           lineHeight: 1.2,
                           backgroundColor: selectedOperatorNumbers.has(opNumber) ? 'primary.main' : 'transparent',
                           color: selectedOperatorNumbers.has(opNumber) ? 'primary.contrastText' : 'primary.main',
                           borderColor: 'primary.main',
                           '&:hover': {
                             backgroundColor: selectedOperatorNumbers.has(opNumber) ? 'primary.dark' : 'primary.light',
                             color: selectedOperatorNumbers.has(opNumber) ? 'primary.contrastText' : 'primary.main',
                           },
                           '@media (hover: none)': {
                             '&:hover': {
                               backgroundColor: selectedOperatorNumbers.has(opNumber) ? 'primary.main' : 'transparent',
                               color: selectedOperatorNumbers.has(opNumber) ? 'primary.contrastText' : 'primary.main',
                             }
                           }
                         }}
                      >
                        {opNumber}
                      </Button>
                    ))}
                </Box>
                  </Box>
                </>
              )}
            </Box>

            <Divider />

            {/* Lease Names Section */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1">
                  Lease Name
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({selectedLeaseNames.size} selected of {getAvailableFilterOptions().leaseNames.length})
                  </Typography>
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={enabledParameters.leaseName}
                      onChange={() => toggleParameterEnabled('leaseName')}
                      disabled={enabledParameters.apiNumber}
                      size="small"
                    />
                  }
                  label=""
                  sx={{ m: 0 }}
                />
              </Box>
              {enabledParameters.leaseName && (
                <>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={leaseNameSearch}
                  onChange={(e) => setLeaseNameSearch(e.target.value)}
                  sx={{ flexGrow: 1, maxWidth: 300 }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const filteredLeaseNames = getAvailableFilterOptions().leaseNames.filter(leaseName => 
                      leaseName.toLowerCase().includes(leaseNameSearch.toLowerCase())
                    );
                    setSelectedLeaseNames(new Set(filteredLeaseNames));
                    updateSelectionsBasedOnLeaseNames(new Set(filteredLeaseNames));
                  }}
                  sx={{ 
                    textTransform: 'none', 
                    minWidth: 'auto', 
                    px: 1, 
                    py: 0.5, 
                    fontSize: '0.75rem' 
                  }}
                >
                  ALL
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    // NONE should deselect ALL items for this parameter, not just filtered ones
                    setSelectedLeaseNames(new Set());
                    // Clear all other parameters too since no lease names remain
                    setSelectedCounties(new Set());
                    setSelectedOperators(new Set());
                    setSelectedOperatorNumbers(new Set());
                    setSelectedLeaseNumbers(new Set());
                  }}
                  sx={{ 
                    textTransform: 'none', 
                    minWidth: 'auto', 
                    px: 1, 
                    py: 0.5, 
                    fontSize: '0.75rem' 
                  }}
                >
                  NONE
                </Button>
              </Box>
              <Box sx={{ 
                maxHeight: 200, 
                overflow: 'auto', 
                border: 1, 
                borderColor: 'divider', 
                borderRadius: 1,
                p: 1
              }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {getAvailableFilterOptions().leaseNames
                    .filter(leaseName => 
                      leaseName.toLowerCase().includes(leaseNameSearch.toLowerCase())
                    )
                    .map((leaseName) => (
                      <Button
                        key={leaseName}
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          const newSelected = new Set(selectedLeaseNames);
                          if (newSelected.has(leaseName)) {
                            newSelected.delete(leaseName);
                          } else {
                            newSelected.add(leaseName);
                          }
                          setSelectedLeaseNames(newSelected);
                          updateSelectionsBasedOnLeaseNames(newSelected);
                        }}
                         sx={{ 
                           textTransform: 'none',
                           minWidth: 'auto',
                           px: 1.5,
                           py: 0.25,
                           fontSize: '0.75rem',
                           fontWeight: 400,
                           lineHeight: 1.2,
                           backgroundColor: selectedLeaseNames.has(leaseName) ? 'primary.main' : 'transparent',
                           color: selectedLeaseNames.has(leaseName) ? 'primary.contrastText' : 'primary.main',
                           borderColor: 'primary.main',
                           '&:hover': {
                             backgroundColor: selectedLeaseNames.has(leaseName) ? 'primary.dark' : 'primary.light',
                             color: selectedLeaseNames.has(leaseName) ? 'primary.contrastText' : 'primary.main',
                           },
                           '@media (hover: none)': {
                             '&:hover': {
                               backgroundColor: selectedLeaseNames.has(leaseName) ? 'primary.main' : 'transparent',
                               color: selectedLeaseNames.has(leaseName) ? 'primary.contrastText' : 'primary.main',
                             }
                           }
                         }}
                      >
                        {leaseName}
                      </Button>
                    ))}
                </Box>
              </Box>
                </>
              )}
            </Box>

            <Divider />

            {/* Lease Numbers Section */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1">
                  Lease Number
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({selectedLeaseNumbers.size} selected of {getAvailableFilterOptions().leaseNumbers.length})
                  </Typography>
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={enabledParameters.leaseNumber}
                      onChange={() => toggleParameterEnabled('leaseNumber')}
                      disabled={enabledParameters.apiNumber}
                      size="small"
                    />
                  }
                  label=""
                  sx={{ m: 0 }}
                />
              </Box>
              {enabledParameters.leaseNumber && (
                <>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={leaseNumberSearch}
                  onChange={(e) => setLeaseNumberSearch(e.target.value)}
                  sx={{ flexGrow: 1, maxWidth: 300 }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const filteredLeaseNumbers = getAvailableFilterOptions().leaseNumbers.filter(leaseNumber => 
                      leaseNumber.toLowerCase().includes(leaseNumberSearch.toLowerCase())
                    );
                    setSelectedLeaseNumbers(new Set(filteredLeaseNumbers));
                    // Trigger hierarchical filtering for all lease numbers at once
                    const allLeaseNames = new Set();
                    filteredLeaseNumbers.forEach(leaseNumber => {
                      const options = getAvailableFilterOptions();
                      const relationships = options.relationships;
                      relationships.forEach((relationship, leaseName) => {
                        if (relationship.leaseNumbers.has(leaseNumber)) {
                          allLeaseNames.add(leaseName);
                        }
                      });
                    });
                    updateSelectionsBasedOnLeaseNames(allLeaseNames);
                  }}
                  sx={{ 
                    textTransform: 'none', 
                    minWidth: 'auto', 
                    px: 1, 
                    py: 0.5, 
                    fontSize: '0.75rem' 
                  }}
                >
                  ALL
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    // NONE should deselect ALL items for this parameter, not just filtered ones
                    setSelectedLeaseNumbers(new Set());
                    // Clear all other parameters too since no lease numbers remain
                    setSelectedCounties(new Set());
                    setSelectedOperators(new Set());
                    setSelectedOperatorNumbers(new Set());
                    setSelectedLeaseNames(new Set());
                  }}
                  sx={{ 
                    textTransform: 'none', 
                    minWidth: 'auto', 
                    px: 1, 
                    py: 0.5, 
                    fontSize: '0.75rem' 
                  }}
                >
                  NONE
                </Button>
              </Box>
              <Box sx={{ 
                maxHeight: 200, 
                overflow: 'auto', 
                border: 1, 
                borderColor: 'divider', 
                borderRadius: 1,
                p: 1
              }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {getAvailableFilterOptions().leaseNumbers
                    .filter(leaseNumber => 
                      leaseNumber.toLowerCase().includes(leaseNumberSearch.toLowerCase())
                    )
                    .map((leaseNumber) => (
                      <Button
                        key={leaseNumber}
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          const newSelected = new Set(selectedLeaseNumbers);
                          if (newSelected.has(leaseNumber)) {
                            newSelected.delete(leaseNumber);
                            updateLeaseNamesBasedOnParameter('leaseNumber', leaseNumber, false);
                          } else {
                            newSelected.add(leaseNumber);
                            updateLeaseNamesBasedOnParameter('leaseNumber', leaseNumber, true);
                          }
                          setSelectedLeaseNumbers(newSelected);
                        }}
                         sx={{ 
                           textTransform: 'none',
                           minWidth: 'auto',
                           px: 1.5,
                           py: 0.25,
                           fontSize: '0.75rem',
                           fontWeight: 400,
                           lineHeight: 1.2,
                           backgroundColor: selectedLeaseNumbers.has(leaseNumber) ? 'primary.main' : 'transparent',
                           color: selectedLeaseNumbers.has(leaseNumber) ? 'primary.contrastText' : 'primary.main',
                           borderColor: 'primary.main',
                           '&:hover': {
                             backgroundColor: selectedLeaseNumbers.has(leaseNumber) ? 'primary.dark' : 'primary.light',
                             color: selectedLeaseNumbers.has(leaseNumber) ? 'primary.contrastText' : 'primary.main',
                           },
                           '@media (hover: none)': {
                             '&:hover': {
                               backgroundColor: selectedLeaseNumbers.has(leaseNumber) ? 'primary.main' : 'transparent',
                               color: selectedLeaseNumbers.has(leaseNumber) ? 'primary.contrastText' : 'primary.main',
                             }
                           }
                         }}
                      >
                        {leaseNumber}
                      </Button>
                    ))}
                </Box>
              </Box>
                </>
              )}
            </Box>

            <Divider />

            {/* Well API Numbers Section */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1">
                    Well API Number
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ({selectedApiNumbers.size} selected of {getAvailableFilterOptions().apiNumbers.length})
                    </Typography>
                  </Typography>
                  <Tooltip title="Well API Number refinement is mutually exclusive with other filter options. When enabled, other options are automatically disabled." arrow>
                    <InfoIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </Tooltip>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={enabledParameters.apiNumber}
                      onChange={() => toggleParameterEnabled('apiNumber')}
                      size="small"
                    />
                  }
                  label=""
                  sx={{ m: 0 }}
                />
              </Box>
              {enabledParameters.apiNumber && (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Note:</strong> Well API Number refinement is mutually exclusive with other filter options. 
                      When refining by Well API Number, all other filter options are automatically disabled. 
                      Only API numbers from the Wells table are available for selection.
                    </Typography>
                  </Alert>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                    <TextField
                      size="small"
                      placeholder="Search..."
                      value={apiNumberSearch}
                      onChange={(e) => setApiNumberSearch(e.target.value)}
                      sx={{ flexGrow: 1, maxWidth: 300 }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const filteredApiNumbers = getAvailableFilterOptions().apiNumbers.filter(apiNumber => 
                          apiNumber.toLowerCase().includes(apiNumberSearch.toLowerCase())
                        );
                        setSelectedApiNumbers(new Set(filteredApiNumbers));
                      }}
                      sx={{ 
                        textTransform: 'none', 
                        minWidth: 'auto', 
                        px: 1, 
                        py: 0.5, 
                        fontSize: '0.75rem' 
                      }}
                    >
                      ALL
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setSelectedApiNumbers(new Set());
                      }}
                      sx={{ 
                        textTransform: 'none', 
                        minWidth: 'auto', 
                        px: 1, 
                        py: 0.5, 
                        fontSize: '0.75rem' 
                      }}
                    >
                      NONE
                    </Button>
                  </Box>
                  <Box sx={{ 
                    maxHeight: 200, 
                    overflow: 'auto', 
                    border: 1, 
                    borderColor: 'divider', 
                    borderRadius: 1,
                    p: 1
                  }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {getAvailableFilterOptions().apiNumbers
                        .filter(apiNumber => 
                          apiNumber.toLowerCase().includes(apiNumberSearch.toLowerCase())
                        )
                        .map((apiNumber) => (
                          <Button
                            key={apiNumber}
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              const newSelected = new Set(selectedApiNumbers);
                              if (newSelected.has(apiNumber)) {
                                newSelected.delete(apiNumber);
                              } else {
                                newSelected.add(apiNumber);
                              }
                              setSelectedApiNumbers(newSelected);
                            }}
                            sx={{ 
                              textTransform: 'none',
                              minWidth: 'auto',
                              px: 1.5,
                              py: 0.25,
                              fontSize: '0.75rem',
                              fontWeight: 400,
                              lineHeight: 1.2,
                              backgroundColor: selectedApiNumbers.has(apiNumber) ? 'primary.main' : 'transparent',
                              color: selectedApiNumbers.has(apiNumber) ? 'primary.contrastText' : 'primary.main',
                              borderColor: 'primary.main',
                              '&:hover': {
                                backgroundColor: selectedApiNumbers.has(apiNumber) ? 'primary.dark' : 'primary.light',
                                color: selectedApiNumbers.has(apiNumber) ? 'primary.contrastText' : 'primary.main',
                              },
                              '@media (hover: none)': {
                                '&:hover': {
                                  backgroundColor: selectedApiNumbers.has(apiNumber) ? 'primary.main' : 'transparent',
                                  color: selectedApiNumbers.has(apiNumber) ? 'primary.contrastText' : 'primary.main',
                                }
                              }
                            }}
                          >
                            {apiNumber}
                          </Button>
                        ))}
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 1, pb: 1 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              onClick={() => {
                handleResetRefineSearch();
                setRefineSearchDialogOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleApplyFilters} 
              variant="contained"
              color="primary"
            >
              Apply
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      
      {/* Upgrade Request Dialog */}
      <UpgradeRequestDialog 
        open={upgradeDialogOpen}
        onClose={() => setUpgradeDialogOpen(false)}
      />
    </WrapperComponent>
  );
};

export default SearchForm;
